import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { X, ArrowLeft, RotateCcw, CheckCircle2, AlertCircle, History, Undo2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAILER_API = `${BACKEND_URL}/api/mailer`;

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const isOk = status === 'completed';
  const isFail = status === 'failed';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider',
        isOk && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
        isFail && 'bg-rose-50 text-rose-700 border border-rose-200',
        !isOk && !isFail && 'bg-neutral-100 text-neutral-700 border border-neutral-200',
      )}
      data-testid={`history-status-${status}`}
    >
      {isOk && <CheckCircle2 className="h-3 w-3" />}
      {isFail && <AlertCircle className="h-3 w-3" />}
      {status}
    </span>
  );
}

export default function ImportHistoryDrawer({ open, onClose, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(null); // null | { force: boolean, claimedCount: number, total: number }

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${MAILER_API}/imports`);
      setItems(r.data || []);
    } catch (e) {
      toast.error('Failed to load import history');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await axios.get(`${MAILER_API}/imports/${id}`);
      setDetail(r.data);
    } catch {
      toast.error('Could not load import details');
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadList();
      setSelectedId(null);
      setDetail(null);
    }
  }, [open, loadList]);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
  }, [selectedId, loadDetail]);

  const rerun = async () => {
    if (!selectedId || rerunning) return;
    setRerunning(true);
    try {
      const r = await axios.post(`${MAILER_API}/imports/${selectedId}/rerun`);
      toast.success(`Re-ran import · ${r.data.created} created, ${r.data.duplicates} dupes`);
      onChanged?.();
      await loadList();
      // Jump to the brand-new history row produced by the re-run
      if (r.data.import_id) setSelectedId(r.data.import_id);
    } catch (e) {
      toast.error('Re-run failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRerunning(false);
    }
  };

  // Surgically undo this one import: deletes only the leads it created,
  // its payload chunks, and the import row itself. Honors the 409 guard
  // when leads have already been claimed (force=true to override).
  const revert = async (force = false) => {
    if (!selectedId || reverting) return;
    setReverting(true);
    try {
      const r = await axios.post(`${MAILER_API}/imports/${selectedId}/revert${force ? '?force=true' : ''}`);
      toast.success(`Reverted import · ${r.data.deleted_leads} lead${r.data.deleted_leads === 1 ? '' : 's'} removed`);
      onChanged?.();
      setSelectedId(null);
      setConfirmRevert(null);
      await loadList();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === 'leads_already_claimed') {
        // Promote to a "force?" confirmation rather than just a toast — the
        // LO needs to consciously acknowledge they're yanking claimed work.
        setConfirmRevert({
          force: true,
          claimedCount: detail.claimed_count,
          total: detail.total,
        });
      } else {
        toast.error('Revert failed: ' + (typeof detail === 'string' ? detail : detail?.message || e.message));
      }
    } finally {
      setReverting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={onClose}
      data-testid="import-history-drawer"
    >
      <div
        className="h-full w-full max-w-[640px] bg-white shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="h-14 px-5 border-b hairline flex items-center justify-between bg-neutral-50/40">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-[hsl(var(--kf-gold-deep))]" />
            <div className="display text-[16px] font-bold tracking-tight text-neutral-900">
              {detail ? 'Import details' : 'Import history'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-md hover:bg-neutral-100 inline-flex items-center justify-center text-neutral-500"
            data-testid="import-history-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* List view */}
          {!detail && (
            <div className="divide-y divide-neutral-100">
              {loading && (
                <div className="px-5 py-10 text-center text-[13px] text-neutral-500">Loading…</div>
              )}
              {!loading && items.length === 0 && (
                <div className="px-5 py-10 text-center text-[13px] text-neutral-500">
                  No imports yet. Upload a CSV to start your history.
                </div>
              )}
              {items.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => setSelectedId(row.id)}
                  className="w-full text-left px-5 py-3.5 hover:bg-neutral-50/70 flex items-start justify-between gap-3 transition"
                  data-testid={`import-history-row-${row.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-neutral-900 truncate">{row.label}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5 mono">
                      {fmtDate(row.date)} · {row.total_rows} rows ·{' '}
                      <span className={row.created > 0 ? 'text-emerald-700 font-bold' : 'text-neutral-400'}>{row.created} created</span>
                      {row.refreshed > 0 && (
                        <> · <span className="text-sky-700 font-bold">{row.refreshed} refreshed</span></>
                      )}
                      {row.duplicates > 0 && (
                        <> · <span className="text-amber-700">{row.duplicates} dupes</span></>
                      )}
                    </div>
                  </div>
                  <StatusPill status={row.status} />
                </button>
              ))}
            </div>
          )}

          {/* Detail view */}
          {detail && (
            <div className="px-5 py-5 space-y-4" data-testid="import-history-detail">
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="text-[12px] text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 font-semibold"
                data-testid="import-history-back"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to list
              </button>

              <div className="rounded-xl border hairline divide-y divide-neutral-100">
                {[
                  ['Label',       <span key="v" className="text-right">{detail.label}</span>],
                  ['Date',        <span key="v" className="text-right mono">{fmtDate(detail.date)}</span>],
                  ['Status',      <StatusPill key="v" status={detail.status} />],
                  ['Lead source', <span key="v" className="text-right">{detail.lead_source}</span>],
                  ['Campaign',    <span key="v" className="text-right">{detail.campaign || <span className="text-neutral-400">— none —</span>}</span>],
                  ['Total rows',  <span key="v" className="text-right mono font-bold text-neutral-900">{detail.total_rows}</span>],
                  ['Created',     <span key="v" className="text-right mono font-bold text-emerald-700">{detail.created}</span>],
                  ['Refreshed',   <span key="v" className="text-right mono font-bold text-sky-700">{detail.refreshed || 0}</span>],
                  ['Duplicates',  <span key="v" className="text-right mono font-bold text-amber-700">{detail.duplicates}</span>],
                  ['Skipped',     <span key="v" className="text-right mono font-bold text-neutral-500">{detail.skipped}</span>],
                ].map(([label, value], i) => (
                  <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3 text-[13px]">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-neutral-500">{label}</div>
                    <div className="text-neutral-900 font-semibold text-[13px]">{value}</div>
                  </div>
                ))}
              </div>

              {detail.status === 'failed' && (
                <button
                  type="button"
                  onClick={rerun}
                  disabled={rerunning}
                  data-testid="import-history-rerun"
                  className="w-full h-11 rounded-md btn-ink text-[13px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw className={cn('h-4 w-4', rerunning && 'animate-spin')} />
                  {rerunning ? 'Re-running…' : 'Re-run Import'}
                </button>
              )}

              {/* Revert this specific upload — surgically deletes only
                  the leads this batch created. Available when the
                  import either completed cleanly OR got stuck in the
                  `processing` state (e.g. the uploader tab closed
                  mid-chunk). We treat a stuck processing import as
                  "abort + clean up" so a half-finished batch can be
                  reversed without manual DB work. */}
              {((detail.status === 'completed' || detail.status === 'processing')
                && (detail.created || 0) > 0) && (
                <button
                  type="button"
                  onClick={() => setConfirmRevert({ force: false, claimedCount: 0, total: detail.created, stuck: detail.status === 'processing' })}
                  disabled={reverting}
                  data-testid="import-history-revert"
                  className="w-full h-11 rounded-md border border-rose-200 bg-rose-50/60 hover:bg-rose-100 text-rose-700 text-[13px] font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  <Undo2 className="h-4 w-4" />
                  {detail.status === 'processing' ? 'Abort & revert import' : 'Revert this import'}
                  <span className="text-[11px] font-semibold text-rose-500 ml-1">
                    ({detail.created} lead{detail.created === 1 ? '' : 's'})
                  </span>
                </button>
              )}

              {detail.error_message && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-rose-700 mb-1.5">Error</div>
                  <div className="text-[12px] mono text-rose-900 whitespace-pre-wrap break-words">{detail.error_message}</div>
                </div>
              )}

              {detail.errors?.length > 0 && (
                <div className="rounded-xl border hairline bg-neutral-50/50 p-4">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-neutral-500 mb-1.5">
                    Row warnings ({detail.errors.length})
                  </div>
                  <ul className="mono text-[11px] text-neutral-700 space-y-0.5 list-disc list-inside">
                    {detail.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Revert confirmation modal — two flavors:                            */}
      {/*  • First click: a plain "are you sure" with the lead count.        */}
      {/*  • If the 409 force-required response came back: an escalated      */}
      {/*    warning that some leads are already claimed by an LO.           */}
      {confirmRevert && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-6"
          onClick={() => !reverting && setConfirmRevert(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl border hairline max-w-md w-full p-6"
            data-testid="import-history-revert-confirm"
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                'h-10 w-10 rounded-full flex items-center justify-center',
                confirmRevert.force ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700',
              )}>
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="display text-[20px] font-bold text-neutral-900 tracking-tight">
                {confirmRevert.force ? 'Some leads are already claimed'
                  : confirmRevert.stuck ? 'Abort stuck import?'
                  : 'Revert this import?'}
              </div>
            </div>
            <p className="text-[13px] text-neutral-600 mt-3 leading-relaxed">
              {confirmRevert.force ? (
                <>
                  <span className="font-bold text-neutral-900">{confirmRevert.claimedCount}</span> of the{' '}
                  <span className="font-bold text-neutral-900">{confirmRevert.total}</span> leads from this batch have already
                  been claimed by a loan officer. Reverting will permanently delete those leads — including any notes, history,
                  or work the LO has already done. This cannot be undone.
                </>
              ) : confirmRevert.stuck ? (
                <>
                  This import stalled mid-upload (usually the browser tab closed before the uploader finished all chunks).
                  Aborting will permanently delete the <span className="font-bold text-neutral-900">{confirmRevert.total}</span>{' '}
                  lead{confirmRevert.total === 1 ? '' : 's'} that did land, plus the import record itself, so you can re-upload
                  the full file cleanly. No other leads in your pipeline are touched.
                </>
              ) : (
                <>
                  This permanently deletes the <span className="font-bold text-neutral-900">{confirmRevert.total}</span> lead
                  {confirmRevert.total === 1 ? '' : 's'} created by this upload, plus the import record itself. No other leads
                  in your pipeline are touched. This cannot be undone.
                </>
              )}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setConfirmRevert(null)}
                disabled={reverting}
                className="h-9 px-3 rounded-md border hairline text-[12.5px] font-semibold text-neutral-700 hover:bg-neutral-50"
              >Cancel</button>
              <button
                onClick={() => revert(confirmRevert.force)}
                disabled={reverting}
                className={cn(
                  'h-9 px-3 rounded-md text-white text-[12.5px] font-bold disabled:opacity-60',
                  confirmRevert.force ? 'bg-amber-600 hover:bg-amber-700' : 'bg-rose-600 hover:bg-rose-700',
                )}
                data-testid="import-history-revert-confirm-btn"
              >
                {reverting ? 'Reverting…' : confirmRevert.force ? 'Delete anyway' : 'Yes, revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
